import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { useAfterWindowLoad } from 'shared/hooks';

import * as styles from './styles.css';

interface IGifImageProps {
  imageUrl: string;
  className?: string;
  /**
   * Когда false, GIF не запрашивается (для неактивных слайдов).
   * Загрузка всё равно откладывается до window.load.
   */
  enabled?: boolean;
}

export const GifImage: React.FC<IGifImageProps> = ({ imageUrl, className, enabled = true }) => {
  const isAfterLoad = useAfterWindowLoad();
  const shouldLoad = enabled && isAfterLoad;

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGifLoaded, setIsGifLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hiddenImgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!shouldLoad) {
      return;
    }

    setIsGifLoaded(false);
    setPreviewUrl(null);
    setHasError(false);

    const img = new Image();
    hiddenImgRef.current = img;

    const extractFirstFrame = (): void => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return;
      }

      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      ctx.drawImage(img, 0, 0);

      try {
        const dataUrl = canvas.toDataURL('image/png');
        setPreviewUrl(dataUrl);
      } catch {
        // Cross-origin GIF: preview overlay is unavailable, animated GIF is still shown.
      }
    };

    const handleLoad = (): void => {
      requestAnimationFrame(() => {
        extractFirstFrame();
      });
    };

    const handleError = (): void => {
      setHasError(true);
    };

    img.onload = handleLoad;
    img.onerror = handleError;
    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, shouldLoad]);

  const handleGifLoad = (): void => {
    setIsGifLoaded(true);
  };

  const handleGifError = (): void => {
    setHasError(true);
  };

  if (!enabled) {
    return <div className={className} aria-hidden />;
  }

  const showSkeleton = !isGifLoaded && !hasError;

  if (hasError) {
    return <img src={imageUrl} className={className} alt="" loading="lazy" decoding="async" />;
  }

  return (
    <div className={className} style={{ display: 'inline-block' }}>
      {showSkeleton && (
        <div className={styles['skeleton-wrap']} data-testid="gif-skeleton" aria-hidden>
          <div className={styles['skeleton']} />
        </div>
      )}
      {shouldLoad && (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={handleGifLoad}
          onError={handleGifError}
          className={clsx(styles['image'], { [styles['image_hidden']]: !isGifLoaded })}
        />
      )}
      {previewUrl && !isGifLoaded && <img src={previewUrl} alt="" className={styles['preview']} />}
    </div>
  );
};
